import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../shadcn/ui/dialog';
import { Button } from '../shadcn/ui/button';
import { Input } from '../shadcn/ui/input';
import { Label } from "../shadcn/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../shadcn/ui/table';
import axios from '../axios';
import { useToast } from '../shadcn/ui/use-toast';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import MediaViewDialog from './Dialog/MediaViewDialog';
import MediaDialog from './MediaDialog';
import ReactQuill from 'react-quill';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const Spinner = () => (
    <div className="flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-500"></div>
    </div>
);

const CreateCategoryDialog = ({ isOpenCategoryDialog, onCloseCategoryDialog, onCategoryCreated, parentId }) => {
    const [newCategoryName, setNewCategoryName] = useState('');
    const [smallDescription, setSmallDescription] = useState('');
    const [largeDescription, setLargeDescription] = useState('');
    const [newCategoryImage, setNewCategoryImage] = useState(null); // For new category image
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editedCategoryName, setEditedCategoryName] = useState('');
    const [editedSmallDescription, setEditedSmallDescription] = useState('');
    const [editedLargeDescription, setEditedLargeDescription] = useState('');
    const [editedCategoryImage, setEditedCategoryImage] = useState(null); // For editing category image
    const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [deleteCategoryId, setDeleteCategoryId] = useState(null); // State for the category to delete
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false); // State for the delete confirmation modal
    const { toast } = useToast();

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpenCategoryDialog) {
            fetchCategories(parentId);
        }
    }, [isOpenCategoryDialog]);

    const fetchCategories = async (parentId) => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/categories`, {
                params: {
                    parentId: parentId,
                },
            });

const fetchedCategories = Array.isArray(response.data.data) ? response.data.data : [];
console.log(fetchedCategories); 

        // No transformation – κρατάμε τα raw δεδομένα
        setCategories(fetchedCategories);

    } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch categories',
                variant: 'error',
            });
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCategory = async () => {
        try {
            const formData = new FormData();
            formData.append('name', newCategoryName); // Add name to the payload
            formData.append('parent_id', parentId);

            if (smallDescription) {
                formData.append('description[small_description]', smallDescription); // Add small description to the payload
            }

            if (largeDescription) {
                formData.append('description[large_description]', largeDescription);
            }

            if (newCategoryImage) {
                formData.append('image', newCategoryImage.id);
            }

            const response = await axios.post('/api/categories', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Check if the response indicates success
            if (response.status === 200 || response.status === 201) {
                const category = response.data;

                const newCategory = {
                    ...category,
                    media: newCategoryImage || null, // Store the media object
                };

                setCategories((prevCategories) => [...prevCategories, newCategory]);
                resetForm();

                if (onCategoryCreated) {
                    onCategoryCreated(newCategory);
                }

                toast({
                    title: 'Success',
                    description: 'Η κατηγορία δημιουργήθηκε με επιτυχία',
                    variant: 'success',
                });

                handleCloseForm();
            } else {
                throw new Error('Unexpected response status');
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || 'Αποτυχία δημιουργίας κατηγορίας. Παρακαλώ δοκιμάστε ξανά.';
            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'error',
            });
            console.error('Error creating category:', error); // Log the error for debugging
        }
    };

    const handleUpdateCategory = async () => {
        if (!editingCategory) return;

        try {
            const formData = new FormData();
            formData.append('name', editedCategoryName); // Add name to the payload

            if (editedSmallDescription) {
                formData.append('description[small_description]', editedSmallDescription); // Add small description to the payload
            }

            if (editedLargeDescription) {
                formData.append('description[large_description]', editedLargeDescription);
            }

            if (editedCategoryImage) {
                formData.append('image', editedCategoryImage.id); // Add the updated image
            }

            formData.append('_method', 'PUT'); // Use PUT for updating
            const response = await axios.post(`/api/categories/${editingCategory.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            console.log('API update response:', response.data);

            const category = response.data.category;

            const updatedCategory = {
                ...category,
                media: editedCategoryImage !== null ? editedCategoryImage : category.media,
            };

            setCategories((prevCategories) =>
                prevCategories.map((c) =>
                    c.id === updatedCategory.id ? updatedCategory : c
                )
            );

            toast({
                title: 'Success',
                description: 'Η κατηγορία ενημερώθηκε με επιτυχία',
                variant: 'success',
            });

            handleCloseForm();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Αποτυχία ενημέρωσης κατηγορίας';
            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'error',
            });
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        try {
            await axios.delete(`/api/categories/${categoryId}`);
            setCategories((prevCategories) => prevCategories.filter((category) => category.id !== categoryId));
            toast({
                title: 'Success',
                description: 'Η κατηγορία διαγράφηκε με επιτυχία',
                variant: 'success',
            });
        } catch (error) {
            // Extract error message from the response
            const errorMessage =
                error.response?.data?.message || 'Αποτυχία διαγραφής κατηγορίας. Παρακαλώ δοκιμάστε ξανά.';
            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'error',
            });
            console.error('Error deleting category:', error); // Log the error for debugging
        } finally {
            setDeleteCategoryId(null); // Reset the delete category ID
            setIsDeleteDialogOpen(false); // Close the confirmation modal
        }
    };

    const quillRef = useRef(null);

    const handleMediaSelect = (selectedMedia) => {
        console.log('Selected Media:', selectedMedia); // Debug the selected media
        if (selectedMedia && selectedMedia.length > 0) {
            const mediaItem = selectedMedia[0];
            console.log('Media Item Full File Path:', mediaItem.full_file_path); // Debug the correct property
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const imageUrl = mediaItem.full_file_path || `${baseUrl}${mediaItem.file_path}`;

            if (editingCategory) {
                // Update the mediaUrl for the specific category being edited
                setCategories((prevCategories) =>
                    prevCategories.map((category) =>
                        category.id === editingCategory.id
                            ? { ...category }
                            : category
                    )
                );
                setEditedCategoryImage(mediaItem); // Update the image ID for the edited category
            } else {
                setNewCategoryImage(mediaItem); // Set the image for a new category
            }

            setImagePreview(imageUrl); // Update the image preview in the modal
        }
        setIsMediaDialogOpen(false);
    };

    const handleOpenForm = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setEditedCategoryName(category.name);
            setEditedSmallDescription(category.description?.small_description || '');
            setEditedLargeDescription(category.description?.large_description || '');
            setEditedCategoryImage(null); // Reset selected image
            setImagePreview(category.media?.url || category.media?.full_file_path || null); // ✅ Εδώ!
        } else {
            resetForm();
        }
        setIsEditModalOpen(true);
    };

    const resetForm = () => {
        setNewCategoryName('');
        setSmallDescription('');
        setLargeDescription('');
        setNewCategoryImage(null);
        setEditingCategory(null);
        setEditedCategoryName('');
        setEditedSmallDescription('');
        setEditedLargeDescription('');
        setEditedCategoryImage(null);
        setImagePreview(null); // Clear the image preview
    };

    const handleViewMedia = (mediaItem) => {
        console.log(mediaItem);
        setSelectedMedia(mediaItem);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setSelectedMedia(null);
    };

    const openDeleteConfirmation = (categoryId) => {
        setDeleteCategoryId(categoryId); // Set the category ID to delete
        setIsDeleteDialogOpen(true); // Open the confirmation modal
    };

    const cancelDelete = () => {
        setDeleteCategoryId(null); // Reset the delete category ID
        setIsDeleteDialogOpen(false); // Close the confirmation modal
    };

    const handleCloseForm = useCallback(() => {
        setIsEditModalOpen(false);
        setEditingCategory(null);
        setEditedCategoryName('');
    }, []);

    return (
        <>
            <Dialog open={isOpenCategoryDialog} onOpenChange={onCloseCategoryDialog}>
                <DialogContent className="sm:max-w-[900px] w-full overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Διαχείριση Κατηγοριών</DialogTitle>
                        <DialogClose />
                    </DialogHeader>

                    <div className="mb-4 flex justify-end">
                        <Button
                            onClick={() => handleOpenForm()} // Pass no arguments to open the modal for a new category
                            className="w-full sm:w-auto"
                        >
                            Δημιουργία Νέας Κατηγορίας
                        </Button>
                    </div>

                    {/* Categories List */}
                    <div className="mt-4 overflow-x-auto">
                        <h3 className="text-center font-bold">Λίστα Κατηγοριών</h3>
                        <Table className="min-w-full bg-white">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="py-2">Όνομα</TableHead>
                                    {parentId == 1 && (
                                        <TableHead className="py-2">Εικόνα</TableHead>)}
                                    <TableHead className="py-2 float-end">Επεξεργασία/Διαγραφή</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-4">
                                            <Spinner />
                                        </TableCell>
                                    </TableRow>
                                ) : categories.length > 0 ? (
                                    categories.map((category) => (
                                        <TableRow key={category.id}>
                                            <TableCell className="py-2">
                                                {category.name || 'Χωρίς Όνομα'}
                                            </TableCell>
                                            {parentId == 1 && (
                                            <TableCell className="py-2">
                                                {category.media || category.media?.url || category.media?.full_file_path ? (
                                                    <img
                                                        src={category.media?.url || category.media?.full_file_path}
                                                        alt={category.name || 'Category'}
                                                        className="h-16 w-16 rounded-md object-cover"
                                                    />
                                                ) : (
                                                    <span className="text-gray-400">Χωρίς εικόνα</span>
                                                )}
                                            </TableCell>
                                            )}
                                            <TableCell className="py-2 float-end flex space-x-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleOpenForm(category)}
                                                >
                                                    <PencilIcon className="h-5 w-5 text-blue-500" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openDeleteConfirmation(category.id)}
                                                >
                                                    <TrashIcon className="h-5 w-5 text-red-500" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-4">
                                            Δεν υπάρχουν κατηγορίες
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create/Edit Category Modal */}
            <Dialog
                open={isEditModalOpen || !!editingCategory}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        resetForm(); // Reset the form when the modal is closed
                        setIsEditModalOpen(false); // Close the modal
                    }
                }}
            >
                <DialogContent className="sm:max-w-[800px] w-full">
                    <DialogHeader>
                        <DialogTitle>
                            {editingCategory ? 'Επεξεργασία Κατηγορίας' : 'Δημιουργία Κατηγορίας'}
                        </DialogTitle>
                        <DialogClose />
                    </DialogHeader>
                    {/* Scrollable Content */}
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                        <div className="flex justify-between items-start space-x-4">
                            {/* Name Section */}
                            <div className="w-1/2 ml-1">
                                <Label htmlFor="name" className="block text-left pb-3">
                                    Όνομα Κατηγορίας
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="name"
                                        value={editingCategory ? editedCategoryName : newCategoryName}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.length <= 25) { // Set max characters to 25
                                                editingCategory
                                                    ? setEditedCategoryName(value)
                                                    : setNewCategoryName(value);
                                            }
                                        }}
                                        className="border rounded p-2 w-full"
                                        placeholder="Όνομα Κατηγορίας (μέγιστο 25 χαρακτήρες)"
                                    />
                                    <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                        {editingCategory
                                            ? editedCategoryName.length
                                            : newCategoryName.length}
                                        /25
                                    </span>
                                </div>
                            </div>

                            {/* Conditionally Render Image Section */}
                            {parentId == 1 && (
                                <div className="flex flex-col items-center">
                                    <Label htmlFor="image" className="text-left mb-1">
                                        Εικόνα Κατηγορίας
                                        {/* <span className="text-red-500">*</span> */}
                                    </Label>

                                    {/* Image Preview */}
                                    {imagePreview && (
                                        <div className="mb-2">
                                            <img
                                                // onClick={(e) => handleViewMedia({ url: imagePreview })} // Open media viewer
                                                src={imagePreview} // Use the imagePreview state
                                                alt="Preview"
                                                className={`max-h-20 rounded-md cursor-pointer ${errors.image ? 'border-red-500 border-2' : ''}`}
                                            />
                                        </div>
                                    )}

                                    {/* Select or Change Button */}
                                    <Button
                                        variant="outline"
                                        className={`h-10 w-40 ${errors.image ? 'border-red-500' : ''}`}
                                        onClick={() => setIsMediaDialogOpen(true)} // Open media dialog
                                    >
                                        {imagePreview ? 'Αλλαγή εικόνας' : 'Επιλέξτε εικόνα'}
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Conditionally Render Small Description */}
                        {parentId == 1 && (
                            <>
                                <Label htmlFor="small_description" className="block text-left">
                                    Μικρή Περιγραφή
                                </Label>
                                {errors.small_description && (
                                    <p className="text-red-500 text-sm">{errors.small_description}</p>
                                )}
                                <div className="relative">
                                    <textarea
                                        id="small_description"
                                        value={editingCategory ? editedSmallDescription : smallDescription}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.length <= 75) {
                                                editingCategory
                                                    ? setEditedSmallDescription(value)
                                                    : setSmallDescription(value);
                                            }
                                        }}
                                        className="border rounded p-2 w-full h-20 resize-none"
                                        placeholder="Μικρή Περιγραφή (μέγιστο 75 χαρακτήρες)"
                                    />
                                    <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                        {editingCategory
                                            ? editedSmallDescription.length
                                            : smallDescription.length}
                                        /75
                                    </span>
                                </div>
                            </>
                        )}

                        {/* Conditionally Render Large Description */}
                        {parentId == 1 && (
                            <>
                                <Label htmlFor="large_description" className="block text-left pt-10">
                                    Μεγάλη Περιγραφή
                                </Label>
                                <ReactQuill
                                    ref={quillRef}
                                    id="large_description"
                                    value={editingCategory ? editedLargeDescription : largeDescription}
                                    onChange={(value) =>
                                        editingCategory
                                            ? setEditedLargeDescription(value) // Use the value directly
                                            : setLargeDescription(value) // Use the value directly
                                    }
                                    className="h-[200px] pb-3"
                                />
                            </>
                        )}

                        <div className="pt-10 flex justify-center items-center">
                            <Button
                                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                                className="w-80"
                            >
                                {editingCategory ? 'Ενημέρωση' : 'Δημιουργία'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Media View Dialog */}
            <MediaViewDialog
                isOpen={isViewDialogOpen}
                onClose={handleCloseViewDialog}
                mediaItem={selectedMedia}
            />

            <MediaDialog
                isOpenMediaDialog={isMediaDialogOpen}
                onCloseMediaDialog={() => setIsMediaDialogOpen(false)}
                onSelectMedia={handleMediaSelect}
                maxSelection={1}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={cancelDelete}
                onDelete={() => handleDeleteCategory(deleteCategoryId)}
                confirmationTitle="Διαγραφή Κατηγορίας"
                confirmationMessage={`Είστε σίγουροι ότι θέλετε να διαγράψετε την κατηγορία "${categories.find((c) => c.id === deleteCategoryId)?.name}"?`}
            />
        </>
    );
};

export default CreateCategoryDialog;

